import { isEmpty } from 'lodash';
import { Pushkin } from '@fiverr-private/pushkin_js';
import { logger } from '@fiverr-private/obs';
import { globalEventBus } from '@fiverr-private/futile';
import { PushkinClient, RealtimeEventHandler, RealtimeEventHandlerMapping } from './types';

type InitializationState = 'initializing' | 'initialized' | 'failed';

const INITIALIZATION_EVENT = 'pushkin_client_initialized';

export interface PushkinConfiguration {
    appKey: string;
    cluster: string;
    channels?: string[];
}

class RealtimeListener {
    initializationState: InitializationState | null = null;
    pushkin: PushkinClient | null = null;
    source = 'freelancer_page';

    async init(pushkinConfig: PushkinConfiguration, source = 'freelancer_page'): Promise<void> {
        if (!isEmpty(this.pushkin) || this.initializationState === 'initializing') {
            return;
        }

        try {
            this.initializationState = 'initializing';

            this.pushkin = await Pushkin.connect(
                {
                    appKey: pushkinConfig?.appKey,
                    cluster: pushkinConfig?.cluster,
                },
                pushkinConfig?.channels
            );

            this.initializationState = 'initialized';

            this.source = source;

            globalEventBus.deferred(INITIALIZATION_EVENT, this.pushkin);
        } catch (error: any) {
            this.initializationState = 'failed';

            logger.error({
                error,
                description: 'Failed to connect to Pushkin',
                source: this.source,
            });
        }
    }

    async subscribe(channelName: string, handlerMapping: RealtimeEventHandlerMapping): Promise<void> {
        if (!this.pushkin) {
            globalEventBus.on(INITIALIZATION_EVENT, () => {
                this.subscribe(channelName, handlerMapping);
            });

            return;
        }

        try {
            const channel = await this.pushkin.subscribe({
                channelName,
            });

            for (const [eventName, handler] of Object.entries<RealtimeEventHandler>(handlerMapping)) {
                channel.bind(eventName, handler);
            }
        } catch (error: any) {
            logger.error({
                error,
                description: 'Failed to subscribe to Pushkin channel',
                source: this.source,
            });
        }
    }

    async unsubscribe(channelName: string): Promise<void> {
        if (!this.pushkin) {
            logger.warn('Pushkin client is not initialized');

            return;
        }

        try {
            await this.pushkin.unsubscribe(channelName);
        } catch (error: any) {
            logger.error({
                error,
                description: 'Failed to unsubscribe from Pushkin channel',
                source: this.source,
            });
        }
    }
}

export default new RealtimeListener();
